import React, {
    ReactNode, useCallback, useEffect, useId, useMemo,
    useRef,
} from "react";
// eslint-disable-next-line import/no-extraneous-dependencies
import ReactQuill from "react-quill";

import classNames from "classnames";
import { FormFieldReplacementInput, TicketMessageArticleInput, TicketMessageType } from "../../../__generated__/api";
import useOrCreateRef from "../../useOrCreateRef";
import Styles from "./Editor.module.scss";
import useMentions from "./Mentions";
import Toolbar, { MODULES } from "./Toolbar";

// type QuillEditor = InstanceType<(typeof ReactQuill)["Quill"]>;

// eslint-disable-next-line import/no-extraneous-dependencies
import "quill-paste-smart";
import { useChangingHandler } from "../../../use-changing-callback";
import useEmoji from "./Emoji";
import createLinkButton from "./Link";
import useMarkdown from "./Markdown";
import { insertVariables, VariablesInput } from "./Variables";
import { santitize, StripFormatting } from "../../html-render/HtmlRenderer";

export interface MentionedUser {
    id: string;
    name: string;
}
export interface EditorProps {
    onTypeChange?: (type: TicketMessageType) => void;
    type?: TicketMessageType;
    sendButton?: ReactNode;
    isAllowAI?: boolean;
    onChange?: (
        html: string,
        cleanContent: string,
        mentions?: MentionedUser[],
        replacements?: FormFieldReplacementInput[]
    ) => void;
    onAttachmentClicked?: () => void;
    onUploadAttachments?: (files: File[]) => void;
    onArticleSelected?: (articles: TicketMessageArticleInput) => void;
    bottomEditorPanel?: ReactNode;
    value: string;
    onSend?: () => void;
    className?: string;
    rootClassName?: string;
    mentionSourceId?: string;
    metricPathPrefix?: string[];
    placeholder?: string;
    hasBorder?: boolean;
    canFormat?: boolean;
    size?: "auto" | "small" | "medium";
    variables?: VariablesInput;
    isSingleLine?: boolean;
    isRequired?: boolean;
    isAllowHeaders?: boolean
    onBlur?: () => void;
}

const DEFAULT_TEXT = "<p><br></p>";

const Editor = React.forwardRef<ReactQuill, EditorProps>(({
    onTypeChange,
    type,
    sendButton,
    onChange,
    value,
    onAttachmentClicked,
    onUploadAttachments,
    bottomEditorPanel,
    onArticleSelected,
    isAllowAI,
    onSend,
    className,
    rootClassName,
    mentionSourceId,
    placeholder,
    size,
    hasBorder,
    variables,
    canFormat,
    isSingleLine,
    isRequired,
    isAllowHeaders,
    metricPathPrefix,
    onBlur,
}: EditorProps, ref: React.ForwardedRef<ReactQuill>) => {
    const mentions = useMentions({ sourceId: mentionSourceId as string });
    const quillRef = useOrCreateRef<ReactQuill>(ref);
    const markdown = useMarkdown();
    const toolbar = useEmoji();

    const lastValueRef = useRef({
        value,
        isLoadedVariables: false,
    });

    const id = useId().replace(/:/g, "");

    const keyboardHandler = useChangingHandler(() => onSend?.());

    const clipboardFileHandler = useCallback((event: ClipboardEvent) => {
        const data = event.clipboardData;
        if (!data) {
            return;
        }
        if (data.types.includes("Files")) {
            const files = Array.from(data.files);
            onUploadAttachments?.(files);
        } else if (variables?.shouldReplaceVariables && data.types.includes("text/plain")) {
            event.preventDefault();
            data.items[0].getAsString(text => {
                const input = (isSingleLine ? text.replace(/\n/g, " ") : text).trim();
                const html = santitize(insertVariables(input, variables));
                if (html === input) {
                    return;
                }
                const editor = quillRef.current?.getEditor();
                const selection = editor?.getSelection() ?? { index: 0 };
                const selectionStart = Math.max(selection.index - input.length - 1, 0);

                editor?.deleteText(selectionStart, input.length, "silent");
                editor?.clipboard.dangerouslyPasteHTML(
                    selectionStart,
                    html,
                    "silent",
                );
            });
        }
    }, [onUploadAttachments]);

    const editorChangeHandler = useChangingHandler((html: string) => {
        if (onChange) {
            const users = quillRef.current?.getEditor().getContents().ops?.mapFilter(operation => (operation.insert.mention ? {
                id: operation.insert.mention.id as string,
                name: operation.insert.mention.name as string,
            } : undefined)).dedupe(row => row.id);

            const selectedVariables: FormFieldReplacementInput[] = [];
            quillRef.current?.getEditor().root.querySelectorAll("variable").forEach(element => {
                const replacement = element.attributes.getNamedItem("data-replacement")?.value;
                if (replacement) {
                    selectedVariables.push({
                        fieldId: element.attributes.getNamedItem("data-field-id")?.value,
                        groupId: element.attributes.getNamedItem("data-group-id")?.value,
                        replacement,
                    });
                }
            });

            const content = canFormat ? html : santitize(html, {
                stripFormatting: StripFormatting.ALL,
                shouldAllowMedia: false,
            });
            if (lastValueRef.current.value !== html) {
                lastValueRef.current.value = html;
                onChange(html === DEFAULT_TEXT ? "" : html, content, users, selectedVariables);
            }
        }
    });

    const config = useMemo(() => ({
        id,
        modules: {
            ...toolbar,
            ...mentions,
            ...(canFormat ? markdown : {}),
            keyboard: {
                bindings: {
                    newLine: isSingleLine ? {
                        key: "Enter",
                        handler: () => false,
                    } : undefined,
                    send: {
                        key: "Enter",
                        shortKey: true,
                        handler: keyboardHandler,
                    },
                    link: canFormat ? {
                        key: "k",
                        shortKey: true,
                        handler: createLinkButton(),
                    } : undefined,
                },
            },
            clipboard: {
                allowed: {
                    tags: ["a", "b", "strong", "u", "s", "i", "p", "br", "ul", "ol", "li", "span", "mention"],
                    attributes: ["href", "rel", "target", "class"],
                },
                keepSelection: false,
                substituteBlockElements: true,
                magicPasteLinks: false,
                // newLines: !isSingleLine,
            },
            toolbar: canFormat ? {
                container: `#${id}`,
                handlers: {
                    ...MODULES,
                },
            } : false,
        },
    }), []);

    useEffect(() => {
        if (variables?.shouldReplaceVariables
            && (!lastValueRef.current.isLoadedVariables || lastValueRef.current?.value !== value)) {
            lastValueRef.current.isLoadedVariables = true;

            editorChangeHandler?.(insertVariables(value, variables));
        }
    }, [variables, value]);

    useEffect(() => {
        quillRef.current?.getEditor().root.addEventListener("paste", clipboardFileHandler);
        return () => quillRef.current?.getEditor().root.removeEventListener("paste", clipboardFileHandler);
    }, [quillRef]);

    useEffect(() => {
        if (!variables?.shouldReplaceVariables) {
            return;
        }

        function handleDocumentMouseDown(event: MouseEvent) {
            const editor = quillRef.current?.getEditor();
            if (!editor) {
                return;
            }

            const { root } = editor;
            const currentHtml = editor.getText();

            if (!root || !currentHtml || !onChange || !onBlur) {
                return;
            }

            if (event.target instanceof Node && !root.contains(event.target)) {
                editorChangeHandler(currentHtml);
                onBlur();
            }
        }

        document.addEventListener("mousedown", handleDocumentMouseDown, { capture: true });
        // eslint-disable-next-line consistent-return
        return () => document.removeEventListener("mousedown", handleDocumentMouseDown, { capture: true });
    }, [quillRef, variables, value]);

    return (
        <div
            className={classNames(
                Styles.editor,
                size ? Styles[`editor-${size}`] : "",
                hasBorder ? Styles["editor-border"] : "",
                rootClassName,
            )}
        >
            <ReactQuill
                theme="snow"
                ref={quillRef}
                modules={config.modules}
                placeholder={placeholder}
                value={value}
                onChange={editorChangeHandler}
                className={className}
                readOnly={onChange == null}
            />
            {isRequired && (
                <input type="text" value={value} required className={Styles["editor-required"]} />
            )}
            {bottomEditorPanel}
            {onChange && (
                <Toolbar
                    id={config.id}
                    allowAI={isAllowAI}
                    isAllowHeaders={isAllowHeaders}
                    onArticleSelected={onArticleSelected}
                    onCannedMessage={message => editorChangeHandler(value + message)}
                    onReplaceText={message => editorChangeHandler(message)}
                    onTypeChange={onTypeChange}
                    onAttachmentClicked={onAttachmentClicked}
                    type={type}
                    canFormat={canFormat}
                    sendButton={sendButton}
                    variables={variables}
                    quillRef={quillRef}
                    metricPathPrefix={metricPathPrefix}
                />
            )}

        </div>
    );
});

Editor.defaultProps = {
    onTypeChange: undefined,
    type: TicketMessageType.AgentMessage,
    sendButton: undefined,
    bottomEditorPanel: undefined,
    isAllowAI: false,
    onArticleSelected: undefined,
    onAttachmentClicked: undefined,
    onUploadAttachments: undefined,
    onSend: undefined,
    className: undefined,
    rootClassName: undefined,
    mentionSourceId: undefined,
    onChange: undefined,
    placeholder: "Write a message...",
    size: "auto",
    hasBorder: false,
    variables: undefined,
    canFormat: true,
    isSingleLine: false,
    isRequired: false,
    isAllowHeaders: false,
    metricPathPrefix: undefined,
    onBlur: undefined,
};

export default Editor;
